import React from "react";
import { Star, ShieldCheck, Sparkles, MapPin } from "lucide-react";
import { CTAButton, Reveal, SectionTitle } from "./common";
import AutoplayYouTube from "./AutoplayYouTube";
import { useContent } from "../../context/ContentContext";

export const UrgencyBar = () => {
  const { urgency } = useContent();
  return (
    <div className="bg-[#3a0713] text-[#F4E3B8] text-center py-2 px-4 text-xs sm:text-sm font-medium tracking-wide">
      <Sparkles className="w-3.5 h-3.5 inline mr-1.5 text-[#E9C767] -mt-0.5" />
      {urgency}
    </div>
  );
};

export const Hero = () => {
  const { hero, product } = useContent();
  const defaultBadges = ["Verified Astrologers", "Secure & Confidential", "Insured Delivery"];
  const badges = product?.short_description
    ? (Array.isArray(hero?.badges) && hero.badges.length ? hero.badges : defaultBadges)
    : [];
  const price = product?.final_price || product?.price || 0;
  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "radial-gradient(circle at 50% 0%, #7a1226 0%, #5A0B1D 45%, #3a0713 100%)" }}
    >
      <div className="relative max-w-6xl mx-auto px-4 pt-14 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <Reveal>
          <span className="inline-block font-marcellus tracking-[0.3em] text-[#E9C767] text-xs sm:text-sm uppercase mb-4">
            {hero.kicker || "Adrusta Ratnam"}
          </span>
          <h1 className="font-display text-3xl sm:text-5xl font-extrabold leading-[1.1] text-[#F4E3B8]">
            {hero.titleTop || "READY TO RECEIVE"} <span className="gold-shimmer">{hero.titleHighlight || "ABUNDANT WEALTH"}</span>
          </h1>
          <p className="mt-5 text-[#e9d7c0] text-base sm:text-lg leading-relaxed font-light max-w-xl">
            {hero.subtitle}
          </p>

          <div className="flex items-center gap-3 mt-6">
            <div className="flex">
              {[...Array(5)].map((_, i) => (<Star key={i} className="w-5 h-5 fill-[#E9C767] text-[#E9C767]" />))}
            </div>
            <span className="text-[#e9d7c0] text-sm">5.0 · Trusted by thousands</span>
          </div>

          <div className="mt-6 flex flex-wrap items-end gap-4">
            <div>
              <div className="text-[#E9C767] font-display text-4xl font-bold">₹{Number(price).toLocaleString("en-IN")}</div>
              <div className="text-[#c9b59a] text-xs">incl. silver &amp; making · GST added at checkout</div>
            </div>
          </div>

          <div className="mt-8">
            <CTAButton sub={hero.ctaSub || "You will never look back after today in life"} />
          </div>

          <div className="flex flex-wrap gap-4 mt-7">
            {badges.map((b) => (
              <div key={b} className="flex items-center gap-1.5 text-[#e9d7c0] text-xs sm:text-sm">
                <ShieldCheck className="w-4 h-4 text-[#E9C767]" /> {b}
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={150} className="flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-full blur-2xl opacity-40" style={{ background: "radial-gradient(circle,#E9C767,transparent 70%)" }} />
            <img src={(product?.images && product.images[0]) || ""} alt={product?.name} className="relative w-72 sm:w-[22rem] rounded-2xl border-4 border-[#C9992B]/60 shadow-2xl float-y object-cover" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const VideoSection = () => {
  const { video } = useContent();
  const vid = video.youtubeId || "3cTApzuM74I";
  return (
    <section className="bg-[#FBF5E9] py-16">
      <div className="max-w-4xl mx-auto px-4">
        <SectionTitle kicker={video.kicker} title={video.title} />
        <Reveal>
          <div className="relative rounded-2xl overflow-hidden border-4 border-[#C9992B]/40 shadow-xl aspect-video bg-black">
            <AutoplayYouTube id={vid} className="absolute inset-0 w-full h-full" title="Adrusta Ratnam" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const AboutAstrologer = () => {
  const { about } = useContent();
  return (
    <section className="py-16" style={{ background: "linear-gradient(180deg,#FBF5E9,#F3E7CE)" }}>
      <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
        <Reveal className="flex justify-center">
          <div className="relative">
            <img src={about.image || "https://cdn.pixabay.com/photo/2025/06/18/08/39/golden-lotus-9666694_1280.png"} alt={about.name || "Guruji"} className="w-80 h-80 object-cover rounded-2xl border-4 border-[#C9992B]/40 shadow-xl" />
            <div className="absolute -bottom-5 -right-4 bg-[#5A0B1D] text-[#F4E3B8] px-5 py-3 rounded-xl shadow-lg max-w-[12rem]">
              <div className="font-display font-bold text-lg">{about.statValue || "Lakhs"}</div>
              <div className="text-xs text-[#e9d7c0]">{about.statLabel || "of blessed customers & counting"}</div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <span className="font-marcellus tracking-[0.25em] text-xs uppercase text-[#B8860B]">{about.kicker || "Guided By A Master"}</span>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#5A0B1D] mt-2">{about.name}</h2>
          <p className="text-[#8a6d2f] font-marcellus mt-1">{about.title}</p>
          <p className="text-[#4a3a2e] leading-relaxed mt-4">{about.bio}</p>
          <div className="flex items-center gap-2 mt-5 text-[#5A0B1D] text-sm">
            <MapPin className="w-4 h-4 text-[#C9992B]" /> {about.location || "Adrustar Ratnalayam, Hyderabad"}
          </div>
          <div className="mt-7"><CTAButton /></div>
        </Reveal>
      </div>
    </section>
  );
};
